import { useEffect, useState } from 'react'

import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows'
import DirectionsIcon from '@mui/icons-material/Directions'
import LocationCityIcon from '@mui/icons-material/LocationCity'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import Autocomplete from '@mui/material/Autocomplete'
import Grid from '@mui/material/Grid'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'

import { useDebounce } from '@hooks/useDebounce'

function SuggestIcon({ k }) {
  switch (k) {
    case 'straat':
      return <DirectionsIcon sx={{ pr: '5px' }} />
    case 'plaats':
      return <LocationCityIcon sx={{ pr: '5px' }} />
    case 'postcode':
      return <LocationOnIcon sx={{ pr: '5px' }} />
    default:
      return <DesktopWindowsIcon sx={{ pr: '5px' }} />
  }
}

type Props = {
  search: string
  options: any[]
  onFilterChange: (
    filter: { search: string; searchOption: any | null },
    submit?: boolean,
    enterKey?: boolean,
  ) => void
  onSearchChange: (search: string) => void
}

export const FilterAutosuggest = ({
  search,
  options,
  onFilterChange,
  onSearchChange,
}: Props) => {
  const [open, setOpen] = useState(false)
  const debouncedSearch = useDebounce(search)

  useEffect(() => {
    onSearchChange(debouncedSearch)
  }, [debouncedSearch, onSearchChange])

  const handleSearchChange = ({ search, searchOption }: any) => {
    if (searchOption) {
      onFilterChange({ search: searchOption.v, searchOption }, true)
    } else {
      onFilterChange({ search, searchOption: null })
    }
  }

  const handleKeyDown = (event) => {
    const ENTER_KEY = 13

    if (event.keyCode === ENTER_KEY) {
      onFilterChange({ search, searchOption: null }, true, true)
      setOpen(false)
    }
  }

  return (
    <Autocomplete
      datatest-id="filter-autosuggest"
      getOptionLabel={(option) =>
        typeof option === 'string' ? option : option.v
      }
      filterOptions={(x) => x}
      options={options}
      disableClearable
      autoComplete
      includeInputInList
      freeSolo
      value={search}
      onChange={(_, searchOption) => handleSearchChange({ searchOption })}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="standard"
          data-testid="filterfield"
          label="Zoeken"
          value={search}
          onChange={(event) =>
            handleSearchChange({ search: event.target.value })
          }
          onKeyDown={handleKeyDown}
        />
      )}
      renderOption={(props, option) => {
        const { key, ...rest } = props

        return (
          <li key={key} {...rest}>
            <Grid
              container
              sx={{
                alignItems: 'center',
              }}
            >
              <Grid>
                <SuggestIcon {...option} />
              </Grid>
              <Grid size="grow">
                {option.parts.map((part, index) => (
                  <span
                    key={index}
                    // eslint-disable-next-line no-magic-numbers
                    style={{ fontWeight: part.highlight ? 700 : 400 }}
                  >
                    {part.text}
                  </span>
                ))}

                <Typography variant="body2" color="textSecondary">
                  {option.k}
                </Typography>
              </Grid>
            </Grid>
          </li>
        )
      }}
    />
  )
}
